/**
 * Page Designer RTF Text V1 — static formatted HTML only.
 *
 * This is not the Generic Report Designer editor. There are no placeholders,
 * EMR tokens, images, logos, or patient/encounter/provider context.
 */

export const RTF_FONT_SIZES = Object.freeze([
  Object.freeze({ value: "2", label: "Small" }),
  Object.freeze({ value: "3", label: "Normal" }),
  Object.freeze({ value: "4", label: "Medium" }),
  Object.freeze({ value: "5", label: "Large" }),
  Object.freeze({ value: "6", label: "X-Large" }),
]);

const ALLOWED_TAGS = new Set([
  "B",
  "STRONG",
  "I",
  "EM",
  "U",
  "P",
  "DIV",
  "BR",
  "UL",
  "OL",
  "LI",
  "SPAN",
  "FONT",
]);

const DROP_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "IMG",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "VIDEO",
  "AUDIO",
  "SVG",
  "LINK",
  "META",
  "FORM",
  "INPUT",
  "BUTTON",
  "TEXTAREA",
  "SELECT",
]);

const VOID_TAGS = new Set(["BR"]);

function escapeText(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function isPlaceholderChip(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.hasAttribute("data-placeholder")) return true;
  const className = typeof el.className === "string" ? el.className : "";
  return /\bgrd-ph\b/.test(className);
}

function sanitizeStyle(styleText) {
  if (typeof styleText !== "string" || !styleText.trim()) return "";
  const kept = [];
  styleText.split(";").forEach((part) => {
    const idx = part.indexOf(":");
    if (idx < 0) return;
    const prop = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).trim();
    if (!value || /expression|url\s*\(|javascript|@import/i.test(value)) return;
    if (prop === "text-align" && /^(left|center|right|justify)$/i.test(value)) {
      kept.push(`text-align: ${value.toLowerCase()}`);
      return;
    }
    if (prop === "font-size" && /^(?:[1-7]|[\d.]+(?:px|pt|em|rem|%))$/i.test(value)) {
      kept.push(`font-size: ${value}`);
      return;
    }
    if (prop === "font-weight") {
      const weight = normalizeFontWeight(value);
      if (weight) kept.push(`font-weight: ${weight}`);
      return;
    }
    if (prop === "font-style" && /^(normal|italic)$/i.test(value)) {
      kept.push(`font-style: ${value.toLowerCase()}`);
      return;
    }
    if (prop === "text-decoration" && /^(none|underline|line-through)$/i.test(value)) {
      kept.push(`text-decoration: ${value.toLowerCase()}`);
    }
  });
  return kept.join("; ");
}

/**
 * Chrome / Tailwind contentEditable often emits `bolder` instead of `bold`.
 * Keep that emphasis; map relative keywords to portable absolute weights.
 */
function normalizeFontWeight(value) {
  const weight = String(value || "").trim().toLowerCase();
  if (weight === "bolder") return "bold";
  if (weight === "lighter") return "normal";
  if (/^(normal|bold|[1-9]00)$/.test(weight)) return weight;
  return "";
}

function fontSizeAttr(el) {
  const size = el.getAttribute("size");
  if (!size) return "";
  const n = Number.parseInt(size, 10);
  if (!Number.isFinite(n) || n < 1 || n > 7) return "";
  return String(n);
}

function serializeNode(node) {
  if (!node) return "";
  if (node.nodeType === 3) return escapeText(node.nodeValue || "");
  if (node.nodeType === 8) return "";
  if (node.nodeType !== 1) return serializeChildren(node);
  const tag = node.tagName;
  if (DROP_TAGS.has(tag)) return "";
  if (isPlaceholderChip(node)) return serializeChildren(node);
  if (!ALLOWED_TAGS.has(tag)) return serializeChildren(node);
  if (VOID_TAGS.has(tag)) return `<${tag.toLowerCase()}>`;
  const attrs = [];
  const style = sanitizeStyle(node.getAttribute("style") || "");
  if (style) attrs.push(`style="${escapeAttr(style)}"`);
  if (tag === "FONT") {
    const size = fontSizeAttr(node);
    if (size) attrs.push(`size="${size}"`);
  }
  const align = (node.getAttribute("align") || "").trim().toLowerCase();
  if ((tag === "P" || tag === "DIV") && /^(left|center|right)$/.test(align)) {
    attrs.push(`align="${align}"`);
  }
  const open = attrs.length ? `<${tag.toLowerCase()} ${attrs.join(" ")}>` : `<${tag.toLowerCase()}>`;
  return `${open}${serializeChildren(node)}</${tag.toLowerCase()}>`;
}

function serializeChildren(node) {
  if (!node || !node.childNodes) return "";
  let out = "";
  node.childNodes.forEach((child) => {
    out += serializeNode(child);
  });
  return out;
}

export function sanitizeRtfHtml(html) {
  if (typeof html !== "string") return "";
  if (!html.trim()) return "";
  if (typeof document === "undefined") return "";
  const template = document.createElement("template");
  template.innerHTML = html;
  return serializeChildren(template.content);
}

export function cardRtfHtml(card) {
  return sanitizeRtfHtml(card?.rtfHtml || "");
}

export function isRtfCard(card) {
  return (card?.contentType || "") === "rtf";
}
